import { ChangeDetectionStrategy, Component } from '@angular/core';

import { AccaBoostConfig, QuickBetConfig } from '@frontend/sports/common/client-config-data-access';
import { Store } from '@ngrx/store';
import { RewardTokenType } from 'packages/sports/web/app/src/tokens-base/token-base.models';
import { Observable, filter, map, switchMap, withLatestFrom } from 'rxjs';

import { BetslipType } from '../../../../core/betslip-type';
import { PickId } from '../../../../core/picks/pick-id';
import { betslipSelectedTypeStateSelector } from '../../../types/selectors';
import { QuickBetPickDetails, QuickBetPickDetailsService } from '../../services/quick-bet-pick-details.service';
import { selectQuickBetPickIds } from './../../quick-bet.selectors';

interface QuickBetSummaryViewModel {
    pickDetails: QuickBetPickDetails | null;
    pickIds: PickId[];
    betslipType: BetslipType | null;
    showTokenSummary: boolean;
}

@Component({
    selector: 'bs-quick-bet-summary',
    templateUrl: './quick-bet-summary.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./quick-bet-summary.component.scss'],
    standalone: false,
})
export class QuickBetSummaryComponent {
    readonly viewModel$: Observable<QuickBetSummaryViewModel>;

    constructor(
        private store: Store,
        private quickBetPickDetailsService: QuickBetPickDetailsService,
        public quickBetConfig: QuickBetConfig,
        private accaBoostConfig: AccaBoostConfig,
    ) {
        this.viewModel$ = this.store.select(selectQuickBetPickIds).pipe(
            withLatestFrom(this.store.select(betslipSelectedTypeStateSelector)),
            filter(([pickIds]) => !!pickIds.length),
            switchMap(([pickIds, betslipType]) => this.mapViewModel(pickIds, betslipType)),
        );
    }

    private mapViewModel(pickIds: PickId[], betslipType: BetslipType | null): Observable<QuickBetSummaryViewModel> {
        return this.quickBetPickDetailsService.model$.pipe(
            map((pickDetails) => {
                const hideTokenSummary =
                    pickDetails?.rewardToken?.rewardTokenType === RewardTokenType.AccaBoost && this.accaBoostConfig.isEnhancedLayoutEnabled;

                return {
                    pickDetails,
                    pickIds,
                    betslipType,
                    showTokenSummary: !!pickDetails && !hideTokenSummary,
                };
            }),
        );
    }
}
